import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Breadcrumbs, { BreadcrumbItem } from '../../Common/Breadcrumbs';
import TeamsTable from '../../Common/TeamsTable';
import TeamsHeader from '../../Common/TeamsHeader';
import ShimmerLoader from '../../Common/ShimmerLoader';
import imageCompression from 'browser-image-compression';
import secureApi from '../../utils/secureApi';
import Select from 'react-select';
import { Team } from '../../types/Team';
import { getTeamsByClubId, TeamData, createTeam, getAgeCategoriesFromTournaments, CreateTeamRequest, getTeamById, updateTeam, TeamDetails, deleteTeam } from '../../api/club/teamService';
import { getPlayersByTeamId } from '../../api/club/playerService';
import { getUserClubIds } from '../../utils/roleBasedAccess';
import { getPaymentPreview, PaymentPreviewResponse } from '../../api/tournament/paymentService';
import { getClubInfo, updateClubInfo, ClubInfo } from '../../api/club/clubService';
import { getTournamentById, Tournament } from '../../api/clubmanager/tournamentService';
import { DeleteModal } from '../../Common/DeleteModal';
import './TeamRegistration.css';
import '../MyClub/ClubInformation/ClubInformation.css';
import { getImageUrl } from '../../utils/imageUrl';

// Import the SFL logo
import sflLogo from '../../assets/images/SFL Logo Blue.png';
// Import the profile photo for club logo


// Extended Tournament interface for TeamRegistration
interface TournamentWithDetails extends Tournament {
  venue?: string;
  coachManagerName?: string;
  teamLogo?: string;
}

interface ClubDetails {
  logo: string;
  name: string;
  emailId: string;
  managerName: string;
  managerContactNum: string;
  location: string;
  isActive: boolean;
}

const TeamRegistration: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const [tournament, setTournament] = useState<TournamentWithDetails | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  
  // ✅ OPTIMIZATION: Team caching state
  const [teamsCache, setTeamsCache] = useState<Map<string, Team>>(new Map());
  const [lastTeamsLoad, setLastTeamsLoad] = useState<number>(0);
  const TEAMS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
  
  // Record payment mode detection
  const isRecordPayment = location.state?.isRecordPayment || false;
  
  const [showClubDetails, setShowClubDetails] = useState(false); // Changed to false to keep collapsed by default
  const [hasClubDetailsChanged, setHasClubDetailsChanged] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamFormData, setTeamFormData] = useState({
    name: '',
    shortName: '',
    logo: '',
    ageGroups: [] as string[],
    coachManagerName: '',
    contactDetails: ''
  });

  const [ageCategories, setAgeCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [logoUploadSuccess, setLogoUploadSuccess] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);

  // Add state to track original form data for change detection
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    shortName: '',
    logo: '',
    ageGroups: [] as string[],
    coachManagerName: '',
    contactDetails: ''
  });

  // Club Details Form
  const [clubDetails, setClubDetails] = useState<ClubDetails>({
    logo: '',
    name: '',
    emailId: '',
    managerName: '',
    managerContactNum: '',
    location: '',
    isActive: true
  });

  // Club data state
  const [clubData, setClubData] = useState<ClubInfo | null>(null);
  const [clubLoading, setClubLoading] = useState(false);
  const [clubSaving, setClubSaving] = useState(false);
  const [clubError, setClubError] = useState<string | null>(null);
  const [clubSuccess, setClubSuccess] = useState<string | null>(null);
  
  // Logo preview state (matching Club Information)
  const [logoPreview, setLogoPreview] = useState<string>('');
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalTeam, setDeleteModalTeam] = useState<Team | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);





  useEffect(() => {
    // Check if we have data from navigation state
    if (location.state) {
      const { tournament: navTournament, clubDetails: navClubDetails, team: navTeam, error: navError } = location.state as any;
      
      // ✅ FIXED: Handle error messages from navigation
      if (navError) {
        console.log('🔍 Navigation error received:', navError);
        setError(navError);
        // Clear the error from location state to prevent it from showing again
        window.history.replaceState({}, document.title);
      }
      
      if (navTournament) {
        setTournament(navTournament);
        // Set age categories from navigation state tournament data
        if (navTournament.ageCategories && navTournament.ageCategories.length > 0) {
          setAgeCategories(navTournament.ageCategories);
        }
      }
      if (navClubDetails) {
        setClubDetails(navClubDetails);
      }
      if (navTeam) {
        // Set team data for editing
        setTeamFormData({
          name: navTeam.name || '',
          shortName: navTeam.shortName || '',
          logo: navTeam.logo || '',
          ageGroups: navTeam.ageGroups || [],
          coachManagerName: navTeam.coachManagerName || '',
          contactDetails: navTeam.contactDetails || ''
        });
        setEditingTeam(navTeam);
      }
      setLoading(false);
    } else {
      // Fallback to loading from API
      loadData();
    }
  }, [location.state]);

  // Fallback function to load age categories if needed
  const loadAgeCategories = async () => {
    try {
      console.log('🔍 Fallback: Loading age categories for tournament ID:', id);
      const categories = await getAgeCategoriesFromTournaments(id || undefined);
      setAgeCategories(categories);
    } catch (error) {
      console.error('❌ Failed to load age categories from fallback:', error);
      // Set default categories if API fails
      const defaultCategories = ['U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18'];
      console.log('🔄 Using default age categories from fallback:', defaultCategories);
      setAgeCategories(defaultCategories);
    }
  };

  // ✅ OPTIMIZED: Smarter data loading with navigation state checks
  const loadData = async () => {
    try {
      console.log('🔄 Starting optimized data loading...');
      
      if (id) {
        console.log('🔍 Loading tournament data for ID:', id);
        
        // ✅ Check if we already have tournament data from navigation state
        if (location.state?.tournament) {
          console.log('💡 Optimization: Using tournament data from navigation state');
          setTournament(location.state.tournament);
          setAgeCategories(location.state.tournament.ageCategories || []);
        } else {
          // Load tournament data from API only if not available in navigation state
        try {
          const tournamentResponse = await getTournamentById(id);
          console.log('🔍 Raw tournament API response:', tournamentResponse);
          
          // The clubmanager service returns the tournament directly
          if (tournamentResponse && tournamentResponse._id) {
            const tournamentData: TournamentWithDetails = {
              _id: tournamentResponse._id || id,
              name: tournamentResponse.name || 'Tournament',
              shortName: tournamentResponse.shortName || 'T',
              logo: tournamentResponse.logo || sflLogo,
              location: tournamentResponse.location || 'Location TBD',
              startDate: tournamentResponse.startDate || '2024-08-12',
              endDate: tournamentResponse.endDate || '2024-08-15',
              status: tournamentResponse.status || 'upcoming',
              sports: tournamentResponse.sports || 'Football',
              venue: tournamentResponse.location || 'Venue TBD', // Use location as venue
              coachManagerName: 'Coach TBD', // Not available in this API
              ageCategories: tournamentResponse.ageCategories || ['U16', 'U17', 'U18'],
              isRegistrationOpen: tournamentResponse.isRegistrationOpen ?? true
            };
            console.log('🔍 Setting tournament data:', tournamentData);
            setTournament(tournamentData);
            
            // Set age categories from tournament data
            if (tournamentData.ageCategories && tournamentData.ageCategories.length > 0) {
              setAgeCategories(tournamentData.ageCategories);
            }
          } else {
            // Fallback to mock data if API fails
            console.log('🔍 No valid tournament data found, using fallback');
            const tournamentData: TournamentWithDetails = {
              _id: id,
              name: 'SFL Football Tournament',
              shortName: 'SFL',
              logo: sflLogo,
              location: 'Madiwala, Bengaluru',
              startDate: '2024-08-12',
              endDate: '2024-08-15',
              status: 'upcoming',
              sports: 'Football',
              venue: 'KSFA Stadium, Magrath Road, Ashok Nagar',
              coachManagerName: 'Ramesh Kumar',
              ageCategories: ['U16', 'U17', 'U18'],
              isRegistrationOpen: true,
              isTeamRegistered: false // Add the new field
            };
            setTournament(tournamentData);
            
            // Set age categories from fallback tournament data
            setAgeCategories(tournamentData.ageCategories);
          }
        } catch (tournamentError) {
          console.error('❌ Error loading tournament data:', tournamentError);
          // Fallback to mock data if API fails
          const tournamentData: TournamentWithDetails = {
            _id: id,
            name: 'SFL Football Tournament',
            shortName: 'SFL',
            logo: sflLogo,
            location: 'Madiwala, Bengaluru',
            startDate: '2024-08-12',
            endDate: '2024-08-15',
            status: 'upcoming',
            sports: 'Football',
            venue: 'KSFA Stadium, Magrath Road, Ashok Nagar',
            coachManagerName: 'Ramesh Kumar',
            ageCategories: ['U16', 'U17', 'U18'],
            isRegistrationOpen: true,
            isTeamRegistered: false // Add the new field
          };
          setTournament(tournamentData);
          
          // Set age categories from error fallback data
          setAgeCategories(tournamentData.ageCategories);
          }
        }
      }
      
      // ✅ Load club data and teams efficiently
      const loadPromises: Promise<any>[] = [];
      
      // Add club data loading if not available in navigation state
      if (!location.state?.clubDetails) {
        loadPromises.push(loadClubData());
      } else {
        console.log('💡 Optimization: Using club data from navigation state');
        setClubDetails(location.state.clubDetails);
      }
      
      // Add teams loading if not available in navigation state
      if (!location.state?.teams) {
        loadPromises.push(loadTeams());
      } else {
        console.log('💡 Optimization: Using teams data from navigation state');
        setTeams(location.state.teams);
        setLastTeamsLoad(Date.now());
        
        // Update cache with navigation state teams
        const newCache = new Map<string, Team>();
        location.state.teams.forEach((team: Team) => {
          newCache.set(team._id, team);
        });
        setTeamsCache(newCache);
      }
      
      // Execute remaining loads in parallel
      if (loadPromises.length > 0) {
        console.log('🔄 Executing', loadPromises.length, 'remaining API calls in parallel');
        await Promise.all(loadPromises);
      } else {
        console.log('💡 Optimization: All data available from navigation state, no API calls needed!');
      }
      
    } catch (error) {
      console.error('Error in optimized data loading:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load club data from API
  const loadClubData = async () => {
    try {
      setClubLoading(true);
      setClubError(null);
      
      // Get user's club IDs
      const userData = {
        ...user,
        subRole: role,
        permissions: permissions
      };
      const clubIds = getUserClubIds(userData);
      
      if (clubIds.length === 0) {
        console.log('No club access found for this user');
        return;
      }
      
      // Use the first club ID
      const clubId = clubIds[0];
      
      // Fetch club data from API
      const clubInfo = await getClubInfo(clubId);
      setClubData(clubInfo);
      
      // Update club details form with real data
      setClubDetails({
        logo: clubInfo.logo || '',
        name: clubInfo.name || '',
        emailId: clubInfo.emailId || '',
        managerName: clubInfo.managerName || '',
        managerContactNum: clubInfo.managerContactNum?.toString() || '',
        location: clubInfo.location || '',
        isActive: clubInfo.isActive
      });
      
      // Set logo preview for the upload component
      if (clubInfo.logo) {
        const logoUrl = getImageUrl(clubInfo.logo);
        setLogoPreview(logoUrl);
      }
      
    } catch (error: any) {
      console.error('Error loading club data:', error);
      setClubError(error.message || 'Failed to load club data');
    } finally {
      setClubLoading(false);
    }
  };

  // ✅ OPTIMIZED: Load teams from API with caching
  const loadTeams = async (forceRefresh: boolean = false) => {
    try {
      setTeamsLoading(true);
      setError(null);
      
      // ✅ Check cache first (unless force refresh)
      if (!forceRefresh && !shouldRefreshTeamsCache() && teams.length > 0) {
        console.log('💡 Cache: Using cached teams data, skipping API call');
        setTeamsLoading(false);
        return;
      }
      
      console.log('🔄 Loading teams from API...');
      
      // Get user's club IDs
      const userData = {
        ...user,
        subRole: role,
        permissions: permissions
      };
      const clubIds = getUserClubIds(userData);
      
      if (clubIds.length === 0) {
        setError('No club access found for this user. Please contact your administrator.');
        return;
      }
      
      // Use the first club ID (assuming single club access for now)
      const clubId = clubIds[0];
      
      // Fetch teams from API with tournament ID if available
      const teamsData: TeamData[] = await getTeamsByClubId(clubId, id || undefined);
      
      // Debug: Log team data to see what logos are being returned
      console.log('🔍 Teams data from API:', teamsData);
      teamsData.forEach((team, index) => {
        console.log(`🔍 Team ${index + 1} (${team.name}) logo:`, team.logo);
        console.log(`🔍 Team ${index + 1} (${team.name}) paid players:`, team.paidPlayersCount, 'paid player IDs:', team.paidPlayerIds);
        
        // Enhanced logging for payment data debugging
        console.log(`🔍 Team ${index + 1} (${team.name}) - Payment Data Debug:`, {
          paidPlayerIds: team.paidPlayerIds,
          paidPlayerIdsType: typeof team.paidPlayerIds,
          paidPlayerIdsIsArray: Array.isArray(team.paidPlayerIds),
          paidPlayerIdsLength: team.paidPlayerIds?.length,
          paidPlayersCount: team.paidPlayersCount,
          playersCount: team.playersCount,
          allTeamFields: Object.keys(team)
        });
      });
      
      // Transform API data to match the Team interface
      const transformedTeams: Team[] = teamsData.map(team => {
        console.log('🔍 Processing team for transformation:', {
          teamId: team._id,
          teamName: team.name,
          registration: team.registration,
          hasRegistration: !!team.registration,
          allFields: Object.keys(team),
          // ✅ ADDED: Debug payment fields from API
          totalAmount: team.totalAmount,
          amountPaid: team.amountPaid,
          balanceAmount: team.balanceAmount
        });
        // Calculate payment percentage and status based on paid player IDs
        // Enhanced logic to handle different data types and edge cases
        const paidPlayerIds = team.paidPlayerIds;
        const actualPaidPlayers = Array.isArray(paidPlayerIds) ? paidPlayerIds.length : 
                                 typeof paidPlayerIds === 'string' ? (paidPlayerIds ? 1 : 0) :
                                 paidPlayerIds ? 1 : 0;
        

        const paymentStatus = actualPaidPlayers === 0 ? 'pending' : 
                             actualPaidPlayers === team.playersCount ? 'paid' : 'partial';

        console.log('🔍 Processing team data:', {
          teamId: team._id,
          teamName: team.name,
          playersCount: team.playersCount,
          approvedPlayersCount: team.approvedPlayersCount,
          paidPlayersCount: team.paidPlayersCount || 0,
          actualPaidPlayers: actualPaidPlayers,
          paidPlayerIds: team.paidPlayerIds || [],
          paymentStatus: paymentStatus,
          // Calculate payment status based on actual paid player IDs
          verificationStatus: team.approvedPlayersCount > 0 ? 'verified' : 'pending'
        });

        // Additional debug logging for transformation
        console.log(`🔍 Team ${team.name} - Transformation Debug:`, {
          originalPaidPlayerIds: team.paidPlayerIds,
          calculatedActualPaidPlayers: actualPaidPlayers,
          calculatedPaymentStatus: paymentStatus
        });

        return {
          _id: team._id,
          name: team.name,
          shortName: team.shortName,
          logo: team.logo && team.logo !== '' && !team.logo.includes('association') && !team.logo.includes('banner') ? 
            getImageUrl(team.logo) : 
            '',
          ageGroups: [team.ageCategory], // Convert single ageCategory to array
          coachManagerName: '', // Not provided by API
          contactDetails: '', // Not provided by API
          players: team.playersCount,
          verifiedPlayers: team.approvedPlayersCount, // Real verification data from API
          paidPlayers: actualPaidPlayers, // Use actual paid players count from paidPlayerIds
          paidPlayerIds: team.paidPlayerIds || [], // Store paid player IDs for reference

          paymentStatus: paymentStatus, // Enhanced payment status
          registration: team.registration || null, // Add registration field
          sports: 'Football', // Default value
          status: 'active', // Default value
          playerDetails: [], // Will be populated later if needed
          
          // ✅ ADDED: Payment fields from API response
          totalAmount: team.totalAmount || 0,
          amountPaid: team.amountPaid || 0,
          balanceAmount: team.balanceAmount || 0
        };
      });
      
      setTeams(transformedTeams);
      
      // ✅ Update cache timestamp and cache map
      setLastTeamsLoad(Date.now());
      const newCache = new Map<string, Team>();
      transformedTeams.forEach(team => {
        newCache.set(team._id, team);
      });
      setTeamsCache(newCache);
      
      console.log('💡 Cache: Teams cache updated with', transformedTeams.length, 'teams');
    } catch (error: any) {
      console.error('Error loading teams:', error);
      setError(error.message || 'Failed to load teams. Please try again.');
    } finally {
      setTeamsLoading(false);
    }
  };

  // Note: Payment information is now directly available from the API response
  // via paidPlayerIds and paidPlayersCount, eliminating the need for separate
  // payment preview API calls. This provides more accurate and real-time data.

  useEffect(() => {
    loadData();
  }, []);

  // Debug tournament data changes
  useEffect(() => {
    console.log('🔍 Tournament data changed:', tournament);
  }, [tournament]);

  const handleClubDetailsChange = (field: keyof ClubDetails, value: string) => {
    // Prevent editing of emailId field
    if (field === 'emailId') {
      return;
    }
    
    setClubDetails(prev => ({
      ...prev,
      [field]: value
    }));
    setHasClubDetailsChanged(true);
  };



  // Logo upload click handler (matching Club Information)
  const handleLogoUpload = () => {
    document.getElementById('club-logo-upload')?.click();
  };

  // Logo upload function using presigned URL (matching Club Information)
  const handleLogoFileUpload = async (file: File) => {
    // Validate file
    if (!file.type.startsWith('image/')) {
      setLogoError('Please select a valid image file.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setLogoError('Image size should be less than 5MB.');
      return;
    }

    setLogoError(null);
    setIsLogoUploading(true);
    setLogoUploadProgress(0);
    setLogoUploadSuccess(false);

    try {
      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        onProgress: (progress) => setLogoUploadProgress(progress)
      });

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const dateString = `${dd}-${mm}-${yyyy}`;
      
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpeg';
      const fileName = `club_logo_${Date.now()}.${fileExtension}`;
      const filePath = `my-club/club-logo/${dateString}/${fileName}`;
      const context = `club-logo/${dateString}`;

      // Get presigned URL
      const response = await secureApi.post(
        `/api/v1/upload/presigned-url`,
        {
          fileName,
          filePath,
          contentType: compressedFile.type,
          context
        }
      );

      const presignedUrl = response.data?.presignedUrl;
      const s3Key = response.data?.key;

      if (!presignedUrl) {
        throw new Error('Failed to get presigned URL from server.');
      }

      // Upload to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile
      });

      if (uploadResponse.ok) {
        const imageUrl = getImageUrl(s3Key);
        setClubDetails(prev => ({ ...prev, logo: s3Key }));
        setLogoPreview(imageUrl);
        setLogoUploadSuccess(true);
        setHasClubDetailsChanged(true);
      } else {
        throw new Error('Image upload to S3 failed.');
      }
    } catch (error: any) {
      console.error('Logo upload error:', error);
      setLogoError(error.message || 'Image upload failed. Please try again.');
    } finally {
      setIsLogoUploading(false);
    }
  };

  const handleLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Use the new upload function
    await handleLogoFileUpload(file);
  };

  // Team selection functions - No longer needed since checkboxes are hidden
  /*
  const handleTeamSelection = (teamId: string, selected: boolean) => {
    const team = teams.find(t => t._id === teamId);
    // Only allow selection if team has players
    if (team && team.players > 0) {
      setTeams(prev => prev.map(team =>
        team._id === teamId 
          ? { ...team, isSelected: selected } 
          : { ...team, isSelected: false } // Deselect all other teams
      ));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    // For single selection, select only the first team with players
    if (selected) {
      const firstTeamWithPlayers = teams.find(team => team.players > 0);
      if (firstTeamWithPlayers) {
        setTeams(prev => prev.map(team => ({ 
          ...team, 
          isSelected: team._id === firstTeamWithPlayers._id
        })));
      }
    } else {
      // Deselect all teams
      setTeams(prev => prev.map(team => ({ 
        ...team, 
        isSelected: false 
      })));
    }
  };
  */

  const handleSaveClubDetails = async () => {
    try {
      setClubSaving(true);
      setClubError(null);
      setClubSuccess(null);
      
      // Get user's club IDs
      const userData = {
        ...user,
        subRole: role,
        permissions: permissions
      };
      const clubIds = getUserClubIds(userData);
      
      if (clubIds.length === 0) {
        setClubError('No club access found for this user');
        return;
      }
      
      const clubId = clubIds[0];
      
      // Prepare update payload
      const updatePayload = {
        name: clubDetails.name,
        emailId: clubDetails.emailId,
        managerName: clubDetails.managerName,
        managerContactNum: parseInt(clubDetails.managerContactNum) || 0,
        location: clubDetails.location,
        logo: clubDetails.logo,
        isActive: clubDetails.isActive
      };
      
      // Update club data via API
      const updatedClubInfo = await updateClubInfo(clubId, updatePayload);
      setClubData(updatedClubInfo);
      
      setClubSuccess('Club details updated successfully!');
      setHasClubDetailsChanged(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setClubSuccess(null);
      }, 3000);
      
    } catch (error: any) {
      console.error('Error saving club details:', error);
      setClubError(error.message || 'Failed to save club details');
    } finally {
      setClubSaving(false);
    }
  };

  const handleAddTeam = () => {
    console.log('Add team clicked');
    setEditingTeam(null);
    setTeamFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: ''
    });
    setOriginalFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: ''
    });
    setLogoError(null);
    setIsLogoUploading(false);
    setLogoUploadProgress(0);
    setLogoUploadSuccess(false);
    setShowTeamForm(true);
    
    // Scroll to top smoothly after form is shown
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleEditTeam = async (team: Team) => {
    try {
      setSubmitError(null);
      setShowTeamForm(true);
      setIsEditLoading(true);
      
      // Show loading state for form
      setTeamFormData({
        name: '',
        shortName: '',
        logo: '',
        ageGroups: [],
        coachManagerName: '',
        contactDetails: ''
      });
      
      // Try to fetch detailed team information by ID
      let teamDetails: TeamDetails | null = null;
      try {
        teamDetails = await getTeamById(team._id);
        console.log('Team details fetched from API:', teamDetails);
        console.log('Age category from API:', teamDetails.ageCategory);
        console.log('Age category type:', typeof teamDetails.ageCategory);
        console.log('Is age category array:', Array.isArray(teamDetails.ageCategory));
      } catch (apiError: any) {
        console.warn('API failed, using local team data:', apiError.message);
        // Show a warning message to user
        setSubmitError(`Note: Using cached team data. ${apiError.message}`);
        // Fallback to local team data
        console.log('Using fallback data. Team ageGroups:', team.ageGroups);
        console.log('Team ageGroups type:', typeof team.ageGroups);
        console.log('Is team ageGroups array:', Array.isArray(team.ageGroups));
        
        teamDetails = {
          _id: team._id,
          name: team.name,
          shortName: team.shortName,
          logo: team.logo && team.logo !== '' && !team.logo.includes('association') && !team.logo.includes('banner') ? 
            getImageUrl(team.logo) : 
            '',
          managerName: team.coachManagerName || '',
          countryCode: 91,
          managerContactNum: parseInt(team.contactDetails?.replace(/\D/g, '') || '0'),
          location: 'Bengaluru',
          ageCategory: (Array.isArray(team.ageGroups) ? team.ageGroups : []) as string[],
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }
      
      setEditingTeam(team);
      // Handle both string and array ageCategory from API
      let finalAgeGroups: string[] = [];
      if (Array.isArray(teamDetails.ageCategory)) {
        finalAgeGroups = teamDetails.ageCategory;
      } else if (typeof teamDetails.ageCategory === 'string') {
        const ageCategoryStr = teamDetails.ageCategory as string;
        if (ageCategoryStr.trim() !== '') {
          finalAgeGroups = [ageCategoryStr];
        }
      }
      console.log('Final ageGroups for form:', finalAgeGroups);
      
      const formData = {
        name: teamDetails.name || '',
        shortName: teamDetails.shortName || '',
        logo: teamDetails.logo || '',
        ageGroups: finalAgeGroups,
        coachManagerName: teamDetails.managerName || '',
        contactDetails: teamDetails.managerContactNum ? teamDetails.managerContactNum.toString() : ''
      };
      
      setTeamFormData(formData);
      setOriginalFormData(formData); // Set original data for change detection
      
      setLogoError(null);
      setIsLogoUploading(false);
      setLogoUploadProgress(0);
      setLogoUploadSuccess(false);
      
      // Scroll to top smoothly after form is shown
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    } catch (error: any) {
      console.error('Failed to handle edit team:', error);
      setSubmitError(error.message || 'Failed to load team details');
      setShowTeamForm(false);
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSaveTeam = async () => {
    console.log('Save team:', teamFormData);
    if (editingTeam) {
      // Update existing team (for now, just update local state)
      setTeams(prev => prev.map(team => 
        team._id === editingTeam._id 
          ? { ...team, ...teamFormData }
          : team
      ));
      setShowTeamForm(false);
      setEditingTeam(null);
    } else {
      // Create new team via API
      await createNewTeam();
    }
  };

  const createNewTeam = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Get user's club IDs
      const userData = {
        ...user,
        subRole: role,
        permissions: permissions
      };
      const clubIds = getUserClubIds(userData);
      
      if (clubIds.length === 0) {
        throw new Error('No club access found for this user. Please contact your administrator.');
      }

      // Use the first club ID
      const clubId = clubIds[0];

      // Prepare team data for API
      const teamData: CreateTeamRequest = {
        name: teamFormData.name,
        shortName: teamFormData.shortName,
        logo: teamFormData.logo,
        managerName: teamFormData.coachManagerName,
        countryCode: 91, // Default to India
        managerContactNum: parseInt(teamFormData.contactDetails.replace(/\D/g, '')) || 0,
        location: clubDetails.location || 'Bengaluru',
        ageCategory: teamFormData.ageGroups,
        tournament: id // Include tournament ID for tournament registration context
      };

      // Call API to create team
      const response = await createTeam(clubId, teamData);

      if (response.success) {
        // ✅ FIXED: Force refresh teams list to ensure UI updates
        console.log('💡 Fix: Force refreshing teams list after team creation');
        const newTeam: Team = {
          _id: response.data._id || `temp_${Date.now()}`,
          name: teamFormData.name,
          shortName: teamFormData.shortName,
          logo: teamFormData.logo,
          ageGroups: teamFormData.ageGroups,
          coachManagerName: teamFormData.coachManagerName,
          contactDetails: teamFormData.contactDetails,
          players: 0,
          paidPlayers: 0,
          verifiedPlayers: 0,
          paymentStatus: 'pending',
          registration: null,
          sports: 'Football',
          status: 'active',
          playerDetails: []
        };
        
        // Add to cache AND force refresh to ensure UI updates
        addTeamToCache(newTeam);
        // Force refresh teams from API to get latest data
        await loadTeams(true);
        
        setShowTeamForm(false);
        setEditingTeam(null);
        // Reset form
        setTeamFormData({
          name: '',
          shortName: '',
          logo: '',
          ageGroups: [],
          coachManagerName: '',
          contactDetails: ''
        });
        setLogoError(null);
        setIsLogoUploading(false);
        setLogoUploadProgress(0);
        setLogoUploadSuccess(false);
      } else {
        setSubmitError(response.message);
      }
    } catch (error: any) {
      console.error('Failed to create team:', error);
      setSubmitError(error.message || 'Failed to create team. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelTeam = () => {
    setShowTeamForm(false);
    setEditingTeam(null);
    setTeamFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: ''
    });
    setOriginalFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: ''
    });
    setLogoError(null);
    setIsLogoUploading(false);
    setLogoUploadProgress(0);
    setLogoUploadSuccess(false);
  };

  const handleTeamFormChange = (field: string, value: string) => {
    setTeamFormData(prev => ({ ...prev, [field]: value }));
  };

  // Phone number validation
  const validateContactNumber = (number: string): boolean => {
    const phoneRegex = /^[0-9]{10,15}$/;
    return number === '' || phoneRegex.test(number.replace(/\s/g, ''));
  };

  // Check if form is valid (all required fields are filled)
  const isFormValid = () => {
    const nameValid = teamFormData.name.trim() !== '';
    const shortNameValid = teamFormData.shortName.trim() !== '';
    const ageGroupsValid = Array.isArray(teamFormData.ageGroups) && teamFormData.ageGroups.length > 0;
    const contactValid = teamFormData.contactDetails.trim() !== '' && validateContactNumber(teamFormData.contactDetails);

    return nameValid && shortNameValid && ageGroupsValid && contactValid;
  };

  // Function to check if form data has changed from original
  const hasFormChanged = () => {
    if (!editingTeam) return true; // For new teams, always allow save
    
    return (
      teamFormData.name !== originalFormData.name ||
      teamFormData.shortName !== originalFormData.shortName ||
      teamFormData.logo !== originalFormData.logo ||
      JSON.stringify(teamFormData.ageGroups.sort()) !== JSON.stringify(originalFormData.ageGroups.sort()) ||
      teamFormData.coachManagerName !== originalFormData.coachManagerName ||
      teamFormData.contactDetails !== originalFormData.contactDetails
    );
  };

  const handleTeamLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleTeamLogoUpload = async (file: File) => {
    // Validate file
    if (!file.type.startsWith('image/')) {
      setLogoError('Please select a valid image file.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setLogoError('Image size should be less than 5MB.');
      return;
    }

    setLogoError(null);
    setIsLogoUploading(true);
    setLogoUploadProgress(0);
    setLogoUploadSuccess(false);

    try {
      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        onProgress: (progress) => setLogoUploadProgress(progress)
      });

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = compressedFile.type.split('/')[1] || 'jpg';
      const fileName = `team_logo_${Date.now()}.${ext}`;
      const context = `my-club/team-logo/${dd}-${mm}-${yyyy}`;

      // Get presigned URL
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: compressedFile.type,
          context,
        }
      );

      const presignedUrl = presignRes.data?.presignedUrl;
      const s3Key = presignRes.data?.key;
      
      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile,
      });
      
      if (!uploadRes.ok) {
        throw new Error('Failed to upload image to S3');
      }

      // Update form data with new logo
      setTeamFormData(prev => ({ ...prev, logo: s3Key }));
      setLogoUploadSuccess(true);
      
    } catch (err: any) {
      setLogoError('Logo upload failed. Please try again.');
    } finally {
      setIsLogoUploading(false);
    }
  };

  const handleTeamLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await handleTeamLogoUpload(file);
  };

  const handleManagePlayers = (teamId: string) => {
    console.log('Manage players for team:', teamId);
    // Navigate to player management
    const team = teams.find(t => t._id === teamId);
    if (team) {
      navigate(`/tournaments/${id}/teams/${teamId}/players`, {
        state: { team, tournament, source: 'manage' }
      });
    }
  };

  const handleAddPlayers = (teamId: string) => {
    console.log('🔍 Add players for team:', teamId);
    // Navigate to player management with add source
    const team = teams.find(t => t._id === teamId);
    console.log('🔍 Found team for navigation:', team);
    console.log('🔍 Tournament data for navigation:', tournament);
    
    if (team) {
      navigate(`/tournaments/${id}/teams/${teamId}/players`, {
        state: { team, tournament, source: 'add' }
      });
    } else {
      console.error('❌ Team not found for ID:', teamId);
      console.log('🔍 Available teams:', teams);
    }
  };

  const handleDeleteTeam = (teamId: string) => {
    const team = teams.find(t => t._id === teamId);
    if (team) {
      setDeleteError(null);
      setDeleteModalTeam(team);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteTeam = async () => {
    if (deleteModalTeam) {
      try {
        setIsDeleting(true);
        setDeleteError(null);
        
        // Call the delete API
        await deleteTeam(deleteModalTeam._id);
        
        // Close modal first
        setShowDeleteModal(false);
        setDeleteModalTeam(null);
        setDeleteError(null);
        
        // ✅ OPTIMIZED: Remove from cache instead of full reload
        console.log('💡 Optimization: Removing team from cache instead of full reload');
        removeTeamFromCache(deleteModalTeam._id);
        
      } catch (error: any) {
        console.error('Error deleting team:', error);
        
        // Show specific error message from API
        let errorMessage = 'Failed to delete team. Please try again.';
        
        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.statusMessage) {
            errorMessage = errorData.statusMessage;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setDeleteError(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteModalTeam(null);
    setDeleteError(null);
  };

  // Proceed to payments function - No longer needed since button is hidden
  /*
  const handleProceedToPayments = () => {
    console.log('Proceed to payments');
    // Get selected teams that have players and unpaid players
    const selectedTeams = teams.filter(team => team.isSelected && team.players > 0 && hasUnpaidPlayers(team));
    console.log('Selected teams for payment:', selectedTeams);
    
    // Collect all unpaid players from selected teams
    let allUnpaidPlayers: any[] = [];
    selectedTeams.forEach(team => {
      if (team.playerDetails && team.playerDetails.length > 0) {
        const unpaidPlayers = team.playerDetails.filter(player => (player.amountDue || 0) > 0);
        allUnpaidPlayers = [...allUnpaidPlayers, ...unpaidPlayers];
      }
    });
    
    console.log('All unpaid players:', allUnpaidPlayers);
    console.log('Navigating to:', `/tournaments/${id}/payment`);
    navigate(`/tournaments/${id}/payment`, { 
      state: { 
        selectedTeams,
        unpaidPlayers: allUnpaidPlayers,
        tournament: tournament // Pass tournament data
      } 
    });
  };
  */

  const handlePayNow = async (team: Team) => {
    console.log('🚀 Pay Now clicked for team:', team);
    console.log('🔍 Team registration field:', team.registration);
    console.log('🔍 Team data structure:', {
      _id: team._id,
      name: team.name,
      registration: team.registration,
      hasRegistration: !!team.registration
    });
    
    // ✅ DEBUG: Add console log to confirm button click is working
    console.log('🚀 Pay Now button clicked - function is executing!');
    
    try {
      // Show loading state
      setLoading(true);
      
      // ✅ EXACT SAME LOGIC AS WORKING "Proceed to Payments" button
      console.log('🚀 Using exact same logic as working Proceed to Payments button');
      
      // Get all players for the team (same as PlayerManagement)
      const { playerIds: allPlayerIds, playerDetails: allPlayerDetails, success } = await getTeamPlayersData(team._id);
      
      if (!success) {
        throw new Error('Failed to fetch player data');
      }
      
      console.log('🔍 All player IDs for team:', team.name, allPlayerIds);
      console.log('🔍 All player details loaded:', allPlayerDetails.length, 'players');
      
      // ✅ EXACT SAME LOGIC: Always use all players (same as PlayerManagement)
      const allPlayers = allPlayerDetails;
      
      // Check if team has existing registration (same as PlayerManagement)
      const hasExistingRegistration = !!team.registration;
      console.log('🔍 TeamRegistration - Team registration status:', {
        hasRegistration: hasExistingRegistration,
        registrationId: team.registration,
        teamName: team.name
      });
      
      // Get age category from team data (same as PlayerManagement)
      const ageCategory = team.ageGroups?.[0] || 'U12';
      console.log('🔍 TeamRegistration - Using unified payment-preview API');
      console.log('🔍 Tournament ID:', id);
      console.log('🔍 Team ID:', team._id);
      console.log('🔍 Registration ID:', team.registration || null);
      console.log('🔍 Age Category:', ageCategory);
      console.log('🔍 All Players:', allPlayers.map(player => player._id));
      
      // Prepare payment preview request (EXACT SAME as PlayerManagement)
      const paymentPreviewRequest = {
        registrationId: team.registration || null, // Always send registrationId (null or actual ID)
        ageCategory: ageCategory,
        players: allPlayers.map(player => player._id) // ✅ UNIFIED: Always use all players
      };

      console.log('🔍 Payment preview request:', paymentPreviewRequest);

      // Call payment preview API (EXACT SAME as PlayerManagement)
      const paymentPreviewResponse: PaymentPreviewResponse = await getPaymentPreview(
        id!,
        team._id,
        paymentPreviewRequest
      );

      console.log('🔍 Payment preview response:', paymentPreviewResponse);

      // Validate payment preview data before passing to PaymentPage (EXACT SAME as PlayerManagement)
      const paymentData = paymentPreviewResponse.data;
      console.log('🔍 Validating payment data for PaymentPage:', {
        hasData: !!paymentData,
        totalAmount: paymentData?.totalAmount,
        fullAmount: paymentData?.fullAmount,
        perPlayerFee: paymentData?.perPlayerFee,
        playerCount: paymentData?.playerCount,
        amountPaid: paymentData?.amountPaid,
        minimumAmountToPay: paymentData?.minimumAmountToPay,
        balanceAmount: paymentData?.balanceAmount,
        hasExistingRegistration: hasExistingRegistration
      });

      // Navigate to payment page with appropriate data (EXACT SAME as PlayerManagement)
      navigate(`/tournaments/${id}/payment`, { 
        state: { 
          teamId: team._id,
          team: team,
          selectedPlayers: allPlayers, // ✅ All players
          source: 'teamRegistration', // Different source to identify this flow
          tournament: tournament,
          paymentPreview: paymentData, // Pass complete payment data
          hasExistingRegistration: hasExistingRegistration,
          registrationId: team.registration || null,
          unpaidPlayerIds: allPlayers.map(player => player._id), // ✅ All player IDs
          allPlayers: allPlayers.map(player => player._id), // ✅ All player IDs for unified logic
          realPaymentData: true // Flag to indicate this is real payment data
        }
      });
      
    } catch (error: any) {
      console.error('❌ TeamRegistration - Error in Pay Now flow:', error);
      
      // Check if it's a 403 error (permission issue) - same as PlayerManagement
      if (error.message.includes('403') || error.message.includes('Access denied')) {
        console.log('🔍 TeamRegistration - Payment preview API not available or permission denied, using fallback');
        // Don't show alert for 403 errors, just proceed with fallback
      } else {
        // Show error message to user for other errors
        alert(`Failed to fetch payment details: ${error.message}. Proceeding with estimated payment.`);
      }
      
      // Fallback to existing flow with mock data (same as PlayerManagement)
      navigate(`/tournaments/${id}/payment`, { 
        state: { 
          teamId: team._id,
          team: team,
          selectedPlayers: [], // Empty for fallback
          source: 'teamRegistration',
          tournament: tournament
        } 
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ OPTIMIZED: Single function that returns both IDs and details with deduplication
  const getTeamPlayersData = async (teamId: string): Promise<{
    playerIds: string[];
    playerDetails: any[];
    success: boolean;
  }> => {
    return dedupedApiCall(`players_${teamId}`, async () => {
      try {
        const players = await getPlayersByTeamId(teamId); // SINGLE DEDUPLICATED API CALL
        console.log('🔍 Optimized: Fetched complete player data for team:', teamId, players);
        return {
          playerIds: players.map(player => player._id),
          playerDetails: players,
          success: true
        };
    } catch (error) {
        console.error('❌ Error fetching team players data:', teamId, error);
        return {
          playerIds: [],
          playerDetails: [],
          success: false
        };
      }
    });
  };

  // ✅ DEPRECATED: Keep for backward compatibility but mark as deprecated
  // @deprecated Use getTeamPlayersData instead to avoid duplicate API calls
  const fetchTeamPlayerDetails = async (teamId: string): Promise<any[]> => {
    console.warn('⚠️ DEPRECATED: fetchTeamPlayerDetails is deprecated. Use getTeamPlayersData instead.');
    const { playerDetails } = await getTeamPlayersData(teamId);
    return playerDetails;
  };

  // ✅ DEPRECATED: Keep for backward compatibility but mark as deprecated  
  // @deprecated Use getTeamPlayersData instead to avoid duplicate API calls
  const getPlayerIdsForTeam = async (teamId: string): Promise<string[]> => {
    console.warn('⚠️ DEPRECATED: getPlayerIdsForTeam is deprecated. Use getTeamPlayersData instead.');
    const { playerIds } = await getTeamPlayersData(teamId);
      return playerIds;
  };

  // ✅ OPTIMIZATION: Team cache management functions
  const updateTeamInCache = (updatedTeam: Team) => {
    console.log('💡 Cache: Updating team in cache:', updatedTeam.name);
    setTeams(prevTeams => 
      prevTeams.map(team => 
        team._id === updatedTeam._id ? updatedTeam : team
      )
    );
    
    // Update cache map
    setTeamsCache(prevCache => {
      const newCache = new Map(prevCache);
      newCache.set(updatedTeam._id, updatedTeam);
      return newCache;
    });
  };

  const addTeamToCache = (newTeam: Team) => {
    console.log('💡 Cache: Adding new team to cache:', newTeam.name);
    setTeams(prevTeams => [...prevTeams, newTeam]);
    
    // Update cache map
    setTeamsCache(prevCache => {
      const newCache = new Map(prevCache);
      newCache.set(newTeam._id, newTeam);
      return newCache;
    });
  };

  const removeTeamFromCache = (teamId: string) => {
    console.log('💡 Cache: Removing team from cache:', teamId);
    setTeams(prevTeams => prevTeams.filter(team => team._id !== teamId));
    
    // Update cache map
    setTeamsCache(prevCache => {
      const newCache = new Map(prevCache);
      newCache.delete(teamId);
      return newCache;
    });
  };

  const shouldRefreshTeamsCache = (): boolean => {
    const now = Date.now();
    return (now - lastTeamsLoad) > TEAMS_CACHE_DURATION;
  };

  // ✅ OPTIMIZATION: Request deduplication
  const requestCache = useRef(new Map<string, Promise<any>>());
  
  const dedupedApiCall = async (key: string, apiCall: () => Promise<any>): Promise<any> => {
    if (requestCache.current.has(key)) {
      console.log('💡 Deduplication: Using existing request for key:', key);
      return requestCache.current.get(key)!;
    }
    
    console.log('🔄 Deduplication: Making new request for key:', key);
    const promise = apiCall();
    requestCache.current.set(key, promise);
    
    try {
      const result = await promise;
      requestCache.current.delete(key);
      return result;
    } catch (error) {
      requestCache.current.delete(key);
      throw error;
    }
  };

  // Helper function to check if any player is not paid
  const hasUnpaidPlayers = (team: Team): boolean => {
    // First check if we have detailed player information
    if (team.playerDetails && team.playerDetails.length > 0) {
      // Check if any player has amount due greater than 0
      return team.playerDetails.some(player => (player.amountDue || 0) > 0);
    }
    
    // Fallback: compare total players vs paid players
    // If there are more total players than paid players, there are unpaid players
    if (team.players > 0 && team.paidPlayers !== undefined) {
      return team.players > team.paidPlayers;
    }
    
    // Additional fallback: check team-level payment status
    return team.paymentStatus === 'pending' || (!team.paymentStatus && team.paidPlayers === 0);
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Helper functions for S3 upload
  const generateFilePath = (title: string, date: Date, ext: string) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const sanitizedTitle = title.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    return `club-logos/${year}/${month}/${day}/${sanitizedTitle}_${Date.now()}.${ext}`;
  };

  const generateContext = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `club-logos/${year}/${month}`;
  };

  const getCompressionOptions = (file: File) => {
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 1) {
      return {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        initialQuality: 0.8
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="team-registration-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs 
              items={[
                {
                  label: 'Tournaments',
                  onClick: () => navigate('/tournaments')
                },
                {
                  label: 'Register Teams',
                  active: true
                }
              ]} 
            />
          </div>

          {/* Page Title with Tournament Logo Shimmer */}
          <div className="page-title-section mb-4">
            <div className="d-flex align-items-center">
              <div className="shimmer-circle" style={{ width: '48px', height: '48px', marginRight: '16px' }}></div>
              <div className="shimmer-text" style={{ width: '300px', height: '32px' }}></div>
            </div>
          </div>

          <Row>
            <Col>
              {/* Club Details Shimmer */}
              <ShimmerLoader type="club-details" />
              
              {/* Teams Table Shimmer */}
              <ShimmerLoader type="table-rows" variant="team" rows={5} />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-registration-page">
        <Container fluid>
          <div className="error-container">
            <div className="alert alert-danger" role="alert">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </div>
            <Button variant="primary" onClick={() => loadTeams(true)}>
              <i className="ri-refresh-line me-2"></i>
              Retry
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="team-registration-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              {
                label: 'Tournaments',
                onClick: () => navigate('/tournaments')
              },
              {
                label: 'Register Teams',
                active: true
              }
            ]} 
          />
        </div>

        {/* Page Header with Back Button and Tournament Logo */}
        <div className="page-header-section mb-4" style={{ padding: '0 15px' }}>
          <div className="d-flex align-items-center" style={{ gap: '16px', marginBottom: '24px' }}>
            <button 
              className="back-button"
              onClick={() => navigate('/tournaments')}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <div className="tournament-logo-small" style={{ flexShrink: 0 }}>
              <img 
                src={tournament?.logo || sflLogo} 
                alt={tournament?.name || 'Tournament Logo'}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = sflLogo;
                }}
              />
            </div>
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              <h1 className="page-title mb-0">{tournament?.name || 'SFL League'}</h1>
            </div>
          </div>
        </div>

        <Row>
          <Col>
            {/* Club Details Section */}
            <Card className="club-details-card mb-4">
              {/* Club Error Alert */}
              {clubError && (
                <div className="alert alert-danger m-3" role="alert">
                  <i className="ri-error-warning-line me-2"></i>
                  {clubError}
                </div>
              )}

              {/* Club Success Alert */}
              {clubSuccess && (
                <div className="alert alert-success m-3" role="alert">
                  <i className="ri-check-line me-2"></i>
                  {clubSuccess}
                </div>
              )}
              <Card.Header 
                className="card-header-custom"
                onClick={() => setShowClubDetails(!showClubDetails)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <h4 className="club-details-title mb-0">Club Details</h4>
                    <span className="club-details-optional">(Optional)</span>
                  </div>
                  <Button
                    variant="link"
                    className="collapse-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowClubDetails(!showClubDetails);
                    }}
                  >
                    <i className={`ri-arrow-${showClubDetails ? 'up' : 'down'}-s-line`}></i>
                  </Button>
                </div>
              </Card.Header>
              
              {showClubDetails && (
                <Card.Body>
                  {/* Row 1: Club Logo Only */}
                  <Row className="mb-4">
                    <Col md={12}>
                      <Form.Group className="logo-upload-group">
                        <Form.Label className="form-label">
                          Upload Club Logo
                        </Form.Label>
                        <div className="club-logo-upload">
                          <div 
                            className="logo-preview"
                            style={{
                              background: logoPreview ? `url(${logoPreview}) center/cover` : '#f8f9fa'
                            }}
                            onClick={handleLogoUpload}
                          >
                            {!logoPreview && (
                              <div className="upload-placeholder">
                                <i className="ph-image"></i>
                                <div className="small">Upload Logo</div>
                              </div>
                            )}
                            {isLogoUploading && (
                              <div className="upload-overlay">
                                <div className="spinner-border spinner-border-sm" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                <div className="small mt-2">Uploading... {logoUploadProgress}%</div>
                              </div>
                            )}
                          </div>
                          <div 
                            className="edit-icon"
                            onClick={handleLogoUpload}
                          >
                            <i className="ph-pencil-simple"></i>
                          </div>
                          <input
                            id="club-logo-upload"
                            type="file"
                            accept="image/*"
                            onChange={(e) => e.target.files?.[0] && handleLogoFileChange(e)}
                            style={{ display: 'none' }}
                          />
                        </div>
                        {logoError && (
                          <div className="text-danger small mt-2">{logoError}</div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Row 2: Club Name, Manager Name, Contact Number */}
                  <Row className="mb-4">
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Club Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={clubDetails.name}
                          onChange={(e) => handleClubDetailsChange('name', e.target.value)}
                          placeholder="Enter Club Name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Team/Club Manager Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={clubDetails.managerName}
                          onChange={(e) => handleClubDetailsChange('managerName', e.target.value)}
                          placeholder="Enter Team/Club Manager Name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Contact Number
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          value={clubDetails.managerContactNum}
                          onChange={(e) => handleClubDetailsChange('managerContactNum', e.target.value)}
                          placeholder="Enter Contact Number"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Row 3: Email and Location */}
                  <Row className="mb-4">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Email (Read Only)
                        </Form.Label>
                        <Form.Control
                          type="email"
                          value={clubDetails.emailId}
                          onChange={(e) => handleClubDetailsChange('emailId', e.target.value)}
                          placeholder="Email address"
                          readOnly
                          disabled
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Location</Form.Label>
                        <Form.Control
                          type="text"
                          value={clubDetails.location}
                          onChange={(e) => handleClubDetailsChange('location', e.target.value)}
                          placeholder="Enter Location"
                        />
                      </Form.Group>
                    </Col>
                  </Row>



                  {/* Save Button */}
                  <div className="d-flex justify-content-end">
                    <Button 
                      variant="primary" 
                      onClick={handleSaveClubDetails}
                      disabled={!hasClubDetailsChanged || clubSaving}
                      className="save-club-details-btn"
                    >
                      {clubSaving ? (
                        <>
                          <i className="ri-loader-4-line me-2"></i>
                          Saving...
                        </>
                      ) : (
                        'Save Club Details'
                      )}
                    </Button>
                  </div>
                </Card.Body>
              )}
            </Card>

            {/* Teams Section */}
            <div className="teams-section-container">
              <TeamsHeader
                title="Teams"
                description="Select an existing team or create a new one to proceed with registration"
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                ageGroupFilter={ageGroupFilter}
                onAgeGroupFilterChange={setAgeGroupFilter}
                onAddTeam={handleAddTeam}
                addButtonText="Add New Team"
                showFilters={true}
                ageCategories={ageCategories}
              />
              
              {/* Team Form - Show between Teams header and Team Lineups Card */}
              {showTeamForm && (
                <Card className="team-form-card mb-4">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">{editingTeam ? 'Edit Team Details' : 'Add Team Details'}</h5>
                      <Button 
                        variant="link" 
                        className="close-form-btn"
                        onClick={handleCancelTeam}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    </div>
                    
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Team Logo</Form.Label>
                          <div className="team-logo-upload-section">
                            <div className="team-logo-upload">
                              <div 
                                className="team-logo-preview"
                                style={{
                                  background: teamFormData.logo ? `url(${getImageUrl(teamFormData.logo)}) center/cover` : '#f8f9fa'
                                }}
                                onClick={handleTeamLogoClick}
                              >
                                {!teamFormData.logo && (
                                  <div className="upload-placeholder">
                                    <i className="ri-team-line"></i>
                                    <div className="small">Upload Team Logo</div>
                                  </div>
                                )}
                                {isLogoUploading && (
                                  <div className="upload-overlay">
                                    <div className="spinner-border spinner-border-sm text-light" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <div className="small mt-2 text-light">Uploading... {Math.round(logoUploadProgress)}%</div>
                                  </div>
                                )}
                              </div>
                              {teamFormData.logo && (
                                <div 
                                  className="edit-icon"
                                  onClick={handleTeamLogoClick}
                                >
                                  <i className="ri-pencil-line"></i>
                                </div>
                              )}
                              <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/*"
                                onChange={handleTeamLogoFileChange}
                                style={{ display: 'none' }}
                              />
                            </div>
                            
                            {/* Logo upload error and success */}
                            {logoError && (
                              <div className="document-upload-error">
                                <i className="ri-error-warning-line"></i>
                                {logoError}
                              </div>
                            )}
                            
                            {logoUploadSuccess && (
                              <div className="document-upload-success">
                                <i className="ri-check-line"></i>
                                Team logo uploaded successfully!
                              </div>
                            )}
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Team Name<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={teamFormData.name}
                            onChange={(e) => handleTeamFormChange('name', e.target.value)}
                            placeholder="Enter team name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Abbreviation<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={teamFormData.shortName}
                            onChange={(e) => {
                              // Only allow alphabets and limit to 6 characters
                              const value = e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 6);
                              handleTeamFormChange('shortName', value);
                            }}
                            placeholder="Enter abbreviation (alphabets only, max 6 chars)"
                            maxLength={6}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Age Categories<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                          <Select
                            isMulti={editingTeam === null}
                            classNamePrefix="react-select"
                            value={editingTeam === null 
                              ? (teamFormData.ageGroups || []).map(age => ({ value: age, label: age }))
                              : teamFormData.ageGroups && teamFormData.ageGroups.length > 0 
                                ? { value: teamFormData.ageGroups[0], label: teamFormData.ageGroups[0] }
                                : null
                            }
                            onChange={(selectedOptions) => {
                              if (editingTeam === null) {
                                // Create mode: multi-select
                                const selectedValues = Array.isArray(selectedOptions) ? selectedOptions.map((option: any) => option.value) : [];
                                setTeamFormData(prev => ({ ...prev, ageGroups: selectedValues }));
                              } else {
                                // Edit mode: single-select
                                const selectedValue = selectedOptions && !Array.isArray(selectedOptions) ? (selectedOptions as any).value : '';
                                setTeamFormData(prev => ({ ...prev, ageGroups: selectedValue ? [selectedValue] : [] }));
                              }
                            }}
                            options={ageCategories.map(age => ({ value: age, label: age }))}
                            placeholder={editingTeam === null ? "Select age categories" : "Select age category"}
                            isDisabled={editingTeam !== null && editingTeam.players > 0}
                            maxMenuHeight={400}
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            styles={{
                              control: (base) => ({ 
                                ...base, 
                                minHeight: '45px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                boxShadow: 'none'
                              }),
                              menu: (base) => ({ 
                                ...base, 
                                zIndex: 99999,
                                maxHeight: 400,
                                overflow: 'hidden'
                              }),
                              menuPortal: (base) => ({ 
                                ...base, 
                                zIndex: 99999 
                              }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: 400,
                                padding: 0,
                                overflowY: 'auto',
                                overflowX: 'hidden'
                              }),
                              multiValue: (base) => ({
                                ...base,
                                backgroundColor: '#e3f2fd',
                                borderRadius: '6px'
                              }),
                              multiValueLabel: (base) => ({
                                ...base,
                                color: '#1976d2',
                                fontWeight: '500'
                              }),
                              multiValueRemove: (base) => ({
                                ...base,
                                color: '#1976d2',
                                ':hover': {
                                  backgroundColor: '#bbdefb',
                                  color: '#1565c0'
                                }
                              })
                                                          }}
                            />
                            {editingTeam !== null && editingTeam.players > 0 && (
                              <div className="form-text text-muted mt-1">
                                <i className="ri-information-line me-1"></i>
                                Age category cannot be changed once players are registered to the team
                              </div>
                            )}
                          </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Coach/Manager Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={teamFormData.coachManagerName}
                            onChange={(e) => handleTeamFormChange('coachManagerName', e.target.value)}
                            placeholder="Enter coach/manager name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Contact Details<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                          <Form.Control
                            type="tel"
                            value={teamFormData.contactDetails}
                            onChange={(e) => handleTeamFormChange('contactDetails', e.target.value)}
                            placeholder="Enter contact number"
                            isInvalid={teamFormData.contactDetails.trim() !== '' && !validateContactNumber(teamFormData.contactDetails)}
                          />
                          {teamFormData.contactDetails.trim() !== '' && !validateContactNumber(teamFormData.contactDetails) && (
                            <Form.Control.Feedback type="invalid">
                              Please enter a valid phone number (10-15 digits)
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    {submitError && (
                      <div className="alert alert-danger mb-3" role="alert">
                        <i className="ri-error-warning-line me-2"></i>
                        {submitError}
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-end">
                      <Button variant="secondary" onClick={handleCancelTeam} className="me-2" disabled={isSubmitting}>
                        Cancel
                      </Button>
                      <Button 
                        variant="primary" 
                        onClick={handleSaveTeam}
                        disabled={!isFormValid() || isSubmitting || (editingTeam !== null && !hasFormChanged())}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating...
                          </>
                        ) : (
                          editingTeam ? 'Update Team' : 'Add Team'
                        )}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}
              
              {/* Teams Table using Reusable Component */}
              <TeamsTable
                teams={teams}
                loading={teamsLoading}
                showCheckboxes={false}
                singleSelection={false}
                showAddPlayers={true}
                showManagePlayers={true}
                showViewDetails={false}
                showEdit={true}
                showDelete={true}
                showPayNow={true}
                showPaymentStats={true}
                isRecordPayment={isRecordPayment}
                onAddPlayers={handleAddPlayers}
                onManagePlayers={handleManagePlayers}
                onEdit={handleEditTeam}
                onDelete={handleDeleteTeam}
                onPayNow={handlePayNow}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                ageGroupFilter={ageGroupFilter}
                onAgeGroupFilterChange={setAgeGroupFilter}
                showFilters={false}
                showSearch={false}
                title="Team Lineups"
                emptyMessage="No teams found"
                emptySubMessage="No teams available. Create a new team to get started."
              />
            </div>

            {/* Action Buttons - Hidden */}
            {/* <div className="action-buttons-section">
              <Button 
                variant="dark" 
                onClick={handleProceedToPayments}
                disabled={teams.filter(team => team.isSelected && team.players > 0 && hasUnpaidPlayers(team)).length === 0}
                title={teams.filter(team => team.isSelected && team.players > 0 && hasUnpaidPlayers(team)).length === 0 ? "Select a team with players and unpaid players to proceed" : "Proceed to payment for selected team"}
              >
                Proceed to Payment
              </Button>
            </div> */}
          </Col>
        </Row>
      </Container>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDeleteTeam}
        title="Delete Team"
        message={deleteError || `Are you sure you want to delete "${deleteModalTeam?.name}"? This action cannot be undone.`}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />
    </div>
  );
};

export default TeamRegistration; 